import { DownOutlined, UpOutlined } from '@ant-design/icons';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Flex, Icon } from 'ui';
import { publishSteps as steps } from 'utils/app-configs';
import { publishStepsByStatus } from '../../../utils/app-configs';
import ErrorSummary from '../containers/error-summary-container';
import { showWarningNotification } from './publish-steps';
import {
  ErrorIconContainer,
  ErrorText,
  FinishIcon,
  FinishIconContainer,
  FinishText,
  ProcessIcon,
  ProcessIconContainer,
  ProcessText,
  StyledDivShortSteps,
  StyledFlex,
} from './steps-styles';

const StyledSpan = styled.span`
  text-decoration: underline;
  color: ${p => p.isProcess ?
    p.theme.colors.publishSteps.process :
    p.theme.colors.publishSteps.error} !important;
  font-size: 12px;
  cursor: pointer;
  padding-top: 1px;
`;

const StyledDetails = styled.span`
  color: ${p => p.isProcess ?
    p.theme.colors.publishSteps.process :
    p.theme.colors.publishSteps.error} !important;
  font-size: 12px;
  cursor: pointer;
  padding-top: 1px;
  margin-left: 10px;
`;

export const ErrorIcon = styled(Icon)`
  color: white !important;
`;

const StyledUpIcon = styled(UpOutlined)`
  margin-left: 3px;
  vertical-align: text-bottom;
`;

const StyledDownIcon = styled(DownOutlined)`
  margin-left: 3px;
  vertical-align: text-bottom;
`;

const ShortSteps = ({
  stopPublishStepPoll,
  startPublishStepPoll,
  id,
  type,
  isPolling,
  intl,
  onDataReloaded,
  reloadData,
  initialState = {},
  operationInfo,
  isSidePanel = false,
  error,
  updateId,
  resetOperationInfo,
  closePopover,
  updateBlockOperationInfo,
  openSidepanel,
  errorOrDoneCallback,
  isPlanningBoard = false,
  setStepsHeight,
  needsPolling = true,
  currentBlockStatus = null,
  abortRequest,
  isAborting,
  isGtsBlocked = false,
  gtsErrors = [],
}) => {
  const [wasPolling, setWasPolling] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [currentStatus, setCurrentStatus] = useState({});
  const mounted = useRef(null);
  const containerRef = useRef(null);
  let timerHandle = null;

  useEffect(() => {
    if (setStepsHeight) {
      setStepsHeight(containerRef?.current?.clientHeight || 0);
    }
  }, [containerRef?.current?.clientHeight, isOpen]);

  const checkPolling = () => {
    if (
      needsPolling &&
      id &&
      currentStatus &&
      currentStatus.status < steps.done.status &&
      !currentStatus.isTimedOut &&
      (!currentStatus.errors || (currentStatus.errors && currentStatus.errors.length === 0))
    ) {
      startPublishStepPoll({ id, type, allowUnknown: true });
      setWasPolling(true);
    }
  };

  useEffect(() => {
    if (needsPolling) {
      resetOperationInfo();
      checkPolling();
    }
    return () => {
      if (needsPolling) {
        stopPublishStepPoll();
        setCurrentStatus({});
        resetOperationInfo();
        if (timerHandle) {
          clearTimeout(timerHandle);
          timerHandle = null;
        }
      }
    };
  }, []);

  useEffect(() => {
    if (needsPolling && initialState && Object.keys(initialState).length) {
      setCurrentStatus(initialState);
    }
  }, [initialState]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    } else if (needsPolling && !isPolling && !wasPolling) {
      checkPolling();
    }
  });

  useEffect(() => {
    if (needsPolling && Object.keys(error).length && error.status === 410) {
      stopPublishStepPoll();
      updateId(
        error.actions?.find(a => a.id === 'reload').fromId,
        error.actions?.find(a => a.id === 'reload').toId,
      );
      checkPolling();
    }
  }, [error]);

  useEffect(() => {
    if (needsPolling) {
      if (updateBlockOperationInfo && currentStatus) {
        updateBlockOperationInfo(currentStatus, id);
      }
      if (currentStatus?.errors?.length) {
        if (errorOrDoneCallback) {
          errorOrDoneCallback('error');
        }
        setWasPolling(false);
      }
      if (currentStatus && currentStatus.status === steps.done.status) {
        stopPublishStepPoll();
        if (errorOrDoneCallback) {
          errorOrDoneCallback('done');
        }
        if (reloadData && wasPolling) {
          reloadData(id);
        }
        // When the status is 5 (done) wait 5 seconds
        timerHandle = setTimeout(() => {
          setWasPolling(false);
          setCurrentStatus({});
        }, 5000);
      }
    }
  }, [currentStatus]);

  useEffect(() => {
    if (!needsPolling) {
      setCurrentStatus(currentBlockStatus);
    }
  }, [currentBlockStatus]);

  useEffect(() => {
    if (id) {
      if (
        needsPolling &&
        !isEqual(operationInfo, currentStatus) &&
        operationInfo?.belongsToEntityId === id
      ) {
        if (!currentStatus || !Object.keys(currentStatus).length) {
          setCurrentStatus(operationInfo);
        } else if (
          currentStatus?.processInfo?.hasMultipleProcesses &&
          currentStatus?.processInfo?.currentProcess < operationInfo?.processInfo?.currentProcess
        ) {
          setCurrentStatus(operationInfo);
        } else if (operationInfo?.status >= currentStatus.status) {
          setCurrentStatus(operationInfo);
        } else if (operationInfo?.status === steps.unknown.status) {
          setCurrentStatus(operationInfo);
        } else if (
          // If the next status is smaller but the previous state had errors
          // or the previous status was done,
          // we take the next status as the new one.
          operationInfo?.status < currentStatus.status &&
          ((currentStatus.errors && currentStatus.errors.length) ||
            currentStatus.status === steps.done.status ||
            currentStatus.isTimedOut ||
            currentStatus.isStale)
        ) {
          setCurrentStatus(operationInfo);
        }
      }
    }
  }, [operationInfo, id]);


  const toggleIsOpen = () => {
    setIsOpen(o => !o);
  };

  const doOpenSidepanel = () => {
    if (closePopover) {
      closePopover();
    }
    if (openSidepanel) {
      openSidepanel();
    }
  };

  const renderShowMoreToggle = () =>
    <StyledSpan onClick={toggleIsOpen}>
      {isOpen ?
        <FormattedMessage id='publishSteps.SHOW_LESS' />
        :
        <FormattedMessage id='publishSteps.SHOW_MORE' />
      }
      {isOpen ? <StyledUpIcon /> : <StyledDownIcon />}
    </StyledSpan>;

  const renderShowDetails = (isProcess = false) =>
    <StyledDetails
      isProcess={isProcess}
      onClick={isProcess ?
        () => showWarningNotification(
          intl,
          () => abortRequest({ id, type, statusCode: currentStatus?.status }),
          isAborting,
        ) :
        doOpenSidepanel
      }
    >
      {isProcess ? null : <span>(</span>}
      <StyledSpan isProcess={isProcess}>
        <FormattedMessage id={`publishSteps.${isProcess ? 'ABORT' : 'DETAILS'}`} />
      </StyledSpan>
      {isProcess ? null : <span>)</span>}
    </StyledDetails>;

  const renderCurrentStep = () => {
    if (currentStatus?.errors?.length || currentStatus?.isTimedOut || currentStatus?.isStale) {
      return (
        <StyledFlex ffrn aic jcc>
          {currentStatus?.processInfo?.hasMultipleProcesses ?
            <span>
              {/* eslint-disable-next-line */}
              {`(${currentStatus.processInfo.currentProcess}/${currentStatus.processInfo.totalProcesses}) ${currentStatus.processInfo.currentProcessDescription}`}
            </span> :
            null
          }
          <ErrorIconContainer jcc aic>
            <ErrorIcon type='exclamation' size='sm' />
          </ErrorIconContainer>
          <ErrorText>
            {/* eslint-disable-next-line */}
            {`(${currentStatus?.status}/${Object.keys(publishStepsByStatus).length}) ${intl.formatMessage({
              id: `publishSteps.${publishStepsByStatus[currentStatus?.status]?.title}`,
            })}`
            }
          </ErrorText>
          {isSidePanel ? renderShowMoreToggle() : renderShowDetails()}
        </StyledFlex>
      );
    } else if (currentStatus?.status === steps.done.status) {
      return (
        <StyledFlex ffrn aic jcc>
          {currentStatus?.processInfo?.hasMultipleProcesses ?
            <span>
              {/* eslint-disable-next-line */}
              {`(${currentStatus.processInfo.currentProcess}/${currentStatus.processInfo.totalProcesses}) ${currentStatus.processInfo.currentProcessDescription}`}
            </span> :
            null
          }
          <FinishIconContainer jcc aic>
            <FinishIcon type='check' size='xs' />
          </FinishIconContainer>
          <FinishText>
            {/* eslint-disable-next-line */}
            {`(${currentStatus?.status - 1}/${Object.keys(publishStepsByStatus).length}) ${intl.formatMessage({
              id: `publishSteps.${publishStepsByStatus[currentStatus?.status - 1]?.title}`,
            })}`
            }
          </FinishText>
        </StyledFlex>
      );
    } else if (currentStatus?.status === steps.unknown.status) {
      return (
        <StyledFlex ffrn aic jcc>
          {currentStatus?.processInfo?.hasMultipleProcesses ?
            <span>
              {/* eslint-disable-next-line */}
              {`(${currentStatus.processInfo.currentProcess}/${currentStatus.processInfo.totalProcesses}) ${currentStatus.processInfo.currentProcessDescription}`}
            </span> :
            null
          }
          <ProcessIconContainer jcc aic>
            <ProcessIcon size='xs' />
          </ProcessIconContainer>
        </StyledFlex>
      );
    }
    return (
      <StyledFlex ffrn aic jcc>
        {currentStatus?.processInfo?.hasMultipleProcesses ?
          <span>
            {/* eslint-disable-next-line */}
            {`(${currentStatus.processInfo.currentProcess}/${currentStatus.processInfo.totalProcesses}) ${currentStatus.processInfo.currentProcessDescription}`}
          </span> :
          null
        }
        <ProcessIconContainer jcc aic>
          <ProcessIcon type='loading' size='xs' />
        </ProcessIconContainer>
        <ProcessText>
          {/* eslint-disable-next-line */}
          {`(${currentStatus?.status}/${Object.keys(publishStepsByStatus).length}) ${intl.formatMessage({
            id: `publishSteps.${publishStepsByStatus[currentStatus?.status]?.title}`,
          })}`
          }
        </ProcessText>
        {currentStatus?.isAbortable ? renderShowDetails(true) : null}
      </StyledFlex>
    );
  };

  if (isGtsBlocked) {
    return <StyledDivShortSteps
      isSidePanel={isSidePanel}
      needsBorder={true}
      ref={containerRef}
    >
      <ErrorSummary
        actions={currentStatus?.actions}
        errors={isGtsBlocked ? gtsErrors : currentStatus?.errors}
        isTimedOut={currentStatus?.isTimedOut}
        isStale={currentStatus?.isStale}
        status={currentStatus?.status}
        timeoutInMinutes={currentStatus?.timeoutInMinutes}
        onDataReloaded={onDataReloaded}
        verticalVersion={isSidePanel}
        isPlanningBoard={isPlanningBoard}
        isFromOtherEntity={currentStatus?.isFromOtherEntity}
        otherEntityType={currentStatus?.otherEntityType}
        isGtsBlocked={isGtsBlocked}
        isGtsBlockedPlanning={isGtsBlocked}
        isStandAlone={true}
        resizeCb={open => setIsOpen(open)}
      />
    </StyledDivShortSteps>;
  }

  // If there is no operation info available,
  // don't render the publish steps
  if (!currentStatus || (!currentStatus.status && currentStatus.status !== 0)) {
    return null;
  }

  // If status is done we wait 5 seconds before removing the step component
  if (
    currentStatus.status >= steps.done.status && !wasPolling
  ) {
    return null;
  }

  if (currentStatus.belongsToEntityId !== id) {
    return null;
  }

  return (
    <StyledDivShortSteps
      isSidePanel={isSidePanel}
      needsBorder={isSidePanel &&
        (currentStatus?.errors?.length || currentStatus?.isTimedOut || currentStatus?.isStale)
      }
      ref={containerRef}
    >
      <Flex ffrn jcc>
        {renderCurrentStep()}
      </Flex>
      {isOpen && isSidePanel &&
        <ErrorSummary
          actions={currentStatus?.actions}
          errors={isGtsBlocked ? gtsErrors : currentStatus?.errors}
          isTimedOut={currentStatus?.isTimedOut}
          isStale={currentStatus?.isStale}
          status={currentStatus?.status}
          timeoutInMinutes={currentStatus?.timeoutInMinutes}
          onDataReloaded={onDataReloaded}
          verticalVersion={isSidePanel}
          isPlanningBoard={isPlanningBoard}
          isFromOtherEntity={currentStatus?.isFromOtherEntity}
          otherEntityType={currentStatus?.otherEntityType}
          isGtsBlocked={isGtsBlocked}
          renderShowMoreToggle={renderShowMoreToggle}
          isStandAlone={false}
          resizeCb={open => setIsOpen(open)}
        />
      }
    </StyledDivShortSteps>
  );
};

ShortSteps.propTypes = {
  reloadData: PropTypes.func.isRequired,
  isPolling: PropTypes.bool,
  intl: PropTypes.object,
  startPublishStepPoll: PropTypes.func,
  stopPublishStepPoll: PropTypes.func,
  id: PropTypes.string,
  type: PropTypes.string,
  onDataReloaded: PropTypes.func,
  operationInfo: PropTypes.object,
  isSidePanel: PropTypes.bool,
  resetOperationInfo: PropTypes.func,
  initialState: PropTypes.object,
  error: PropTypes.object,
  updateId: PropTypes.func,
  closePopover: PropTypes.func,
  updateBlockOperationInfo: PropTypes.func,
  openSidepanel: PropTypes.func,
  errorOrDoneCallback: PropTypes.func,
  isPlanningBoard: PropTypes.bool,
  setStepsHeight: PropTypes.func,
  needsPolling: PropTypes.bool,
  currentBlockStatus: PropTypes.object,
  abortRequest: PropTypes.func,
  isAborting: PropTypes.bool,
  isGtsBlocked: PropTypes.bool,
  gtsErrors: PropTypes.array,
};

export default injectIntl(ShortSteps);
